import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import PresignedImage from '../../../components/common/PresignedImage';
import FileTypeIcon from '../../../components/common/FileTypeIcon';
import { useAttachmentPreview } from '../../../components/chat/useAttachmentPreview';
import ForwardMessageModal from '../../../components/chat/ForwardMessageModal';
import { sendMessage } from '../../../store/slices/chatSlice';
import { addNotification } from '../../../store/slices/uiSlice';
import { extractInternalFileKey } from '../../../services/fileUrlService';
import {
  downloadAttachment,
  formatFileSize,
  getFileExtensionLabel,
  openAttachmentFallback,
} from '../../../utils/fileUtils';

const NoteAttachmentViewer = ({ items, index, onIndexChange, onClose }) => {
  const dispatch = useDispatch();
  const currentUserId = useSelector((state) => state.auth?.user?.id);
  const list = Array.isArray(items) ? items : [];
  const currentIndex = Math.min(Math.max(index, 0), Math.max(list.length - 1, 0));
  const current = list[currentIndex] || null;
  const hasMultiple = list.length > 1;
  const { kind, pdfUrl, textContent, textLoading, textError } = useAttachmentPreview(current);
  const [busy, setBusy] = useState('');
  const [showForward, setShowForward] = useState(false);

  const goNext = useCallback(() => {
    if (!hasMultiple) return;
    onIndexChange((currentIndex + 1) % list.length);
  }, [currentIndex, hasMultiple, list.length, onIndexChange]);

  const goPrev = useCallback(() => {
    if (!hasMultiple) return;
    onIndexChange((currentIndex - 1 + list.length) % list.length);
  }, [currentIndex, hasMultiple, list.length, onIndexChange]);

  useEffect(() => {
    const onKeyDown = (event) => {
      if (event.key === 'Escape') {
        event.stopImmediatePropagation();
        if (showForward) {
          setShowForward(false);
          return;
        }
        onClose();
        return;
      }
      if (showForward) return;
      if (event.key === 'ArrowRight') {
        event.preventDefault();
        goNext();
      }
      if (event.key === 'ArrowLeft') {
        event.preventDefault();
        goPrev();
      }
    };
    window.addEventListener('keydown', onKeyDown, true);
    return () => window.removeEventListener('keydown', onKeyDown, true);
  }, [goNext, goPrev, onClose, showForward]);

  if (!current) return null;

  const handleDownload = async () => {
    setBusy('download');
    try {
      await downloadAttachment(current);
    } catch {
      await openAttachmentFallback(current).catch(() => {});
    } finally {
      setBusy('');
    }
  };

  const handleOpen = async () => {
    setBusy('open');
    try {
      await openAttachmentFallback(current);
    } catch {
      // ignore
    } finally {
      setBusy('');
    }
  };

  const handleForwardToChannel = async (targetChannel) => {
    if (!current?.fileUrl || !targetChannel?.id) return;
    setBusy('forward');
    try {
      const fileKey = extractInternalFileKey(current.fileUrl) || current.fileUrl;
      const isDm = targetChannel.type === 'direct' || targetChannel.type === 'DIRECT_MESSAGE';
      const destName = isDm ? 'direct message' : (targetChannel.name || 'channel');
      await dispatch(sendMessage({
        content: '',
        channelId: targetChannel.id,
        senderId: currentUserId,
        attachments: [{
          fileName: current.fileName || 'image',
          fileUrl: fileKey,
          fileType: current.fileType || 'IMAGE',
          fileSize: current.fileSize || 0,
        }],
      })).unwrap();
      dispatch(addNotification({
        type: 'success',
        title: 'Forwarded',
        message: `Image forwarded to ${destName}`,
      }));
      setShowForward(false);
    } catch (error) {
      dispatch(addNotification({
        type: 'error',
        title: 'Could not forward',
        message: error?.data || error?.message || 'Failed to forward image',
      }));
    } finally {
      setBusy('');
    }
  };

  return (
    <div
      className="fixed inset-0 z-[85] flex flex-col bg-black/90"
      role="dialog"
      aria-modal="true"
      aria-label="Attachment viewer"
      onClick={onClose}
    >
      <div
        className="flex items-center gap-2 px-3 py-2 sm:px-4 border-b border-white/10"
        onClick={(event) => event.stopPropagation()}
      >
        <FileTypeIcon fileName={current.fileName} size="sm" />
        <div className="min-w-0 flex-1">
          <p className="text-sm font-medium text-white truncate">{current.fileName || 'File'}</p>
          <p className="text-[11px] text-white/55 truncate">
            {[getFileExtensionLabel(current.fileName), formatFileSize(current.fileSize)]
              .filter(Boolean)
              .join(' · ') || 'Attachment'}
            {hasMultiple ? ` · ${currentIndex + 1} of ${list.length}` : ''}
          </p>
        </div>
        <button
          type="button"
          onClick={handleDownload}
          disabled={Boolean(busy)}
          className="flex-shrink-0 px-3 py-1.5 text-xs font-semibold rounded-lg bg-white text-gray-900 hover:bg-gray-100 disabled:opacity-60"
        >
          {busy === 'download' ? 'Downloading…' : 'Download'}
        </button>
        <button
          type="button"
          onClick={() => setShowForward(true)}
          disabled={Boolean(busy) || !current?.fileUrl}
          className="flex-shrink-0 px-3 py-1.5 text-xs font-semibold rounded-lg border border-white/25 text-white hover:bg-white/10 disabled:opacity-60"
        >
          {busy === 'forward' ? 'Sending…' : 'Forward'}
        </button>
        <button
          type="button"
          onClick={handleOpen}
          disabled={Boolean(busy)}
          className="flex-shrink-0 px-3 py-1.5 text-xs font-semibold rounded-lg border border-white/25 text-white hover:bg-white/10 disabled:opacity-60"
        >
          {busy === 'open' ? 'Opening…' : 'Open'}
        </button>
        {hasMultiple ? (
          <button
            type="button"
            onClick={goNext}
            className="flex-shrink-0 px-3 py-1.5 text-xs font-semibold rounded-lg bg-primary-600 text-white hover:bg-primary-500"
          >
            Next
          </button>
        ) : null}
        <button
          type="button"
          onClick={onClose}
          className="flex-shrink-0 px-2 py-1.5 text-sm font-semibold text-white/80 hover:text-white"
        >
          Close
        </button>
      </div>

      <div
        className="relative flex-1 min-h-0 flex items-center justify-center p-3 sm:p-6"
        onClick={(event) => event.stopPropagation()}
      >
        {hasMultiple ? (
          <button
            type="button"
            onClick={goPrev}
            className="absolute left-2 sm:left-4 top-1/2 -translate-y-1/2 z-10 p-3 text-white bg-black/50 hover:bg-black/70 rounded-full border border-white/20"
            aria-label="Previous"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M15 19l-7-7 7-7" />
            </svg>
          </button>
        ) : null}

        {kind === 'image' ? (
          <PresignedImage
            key={current.fileUrl || current.id}
            fileKey={current.fileUrl}
            alt={current.fileName || ''}
            originalName={current.fileName}
            className="max-h-[calc(100vh-8rem)] max-w-full object-contain rounded-lg shadow-2xl"
          />
        ) : null}

        {kind === 'pdf' ? (
          <div className="w-full max-w-5xl h-full max-h-[calc(100vh-8rem)] rounded-lg overflow-hidden bg-white shadow-2xl">
            {pdfUrl ? (
              <iframe
                title={current.fileName}
                src={`${pdfUrl}#page=1&scrollbar=1&view=FitH`}
                className="w-full h-full min-h-[360px] border-0 bg-white"
              />
            ) : (
              <div className="w-full h-full min-h-[280px] flex items-center justify-center bg-gray-800">
                <span className="text-sm text-white/50">Loading PDF…</span>
              </div>
            )}
          </div>
        ) : null}

        {(kind === 'text' || kind === 'docx') ? (
          <div className="w-full max-w-4xl max-h-[calc(100vh-8rem)] rounded-lg border border-white/20 bg-gray-900 shadow-2xl flex flex-col overflow-hidden">
            {kind === 'docx' && !textLoading && !textError ? (
              <p className="px-4 pt-3 text-[11px] text-white/45">
                Text preview of Word document — layout and images are not shown
              </p>
            ) : null}
            <div className="flex-1 min-h-0 overflow-auto p-4 sm:p-6">
              {textLoading ? (
                <p className="text-sm text-white/50">
                  {kind === 'docx' ? 'Loading document…' : 'Loading text…'}
                </p>
              ) : null}
              {!textLoading && textError ? (
                <p className="text-sm text-white/60">{textError}</p>
              ) : null}
              {!textLoading && !textError && textContent != null ? (
                <pre className="text-sm text-white/90 whitespace-pre-wrap break-words font-mono leading-relaxed">
                  {textContent}
                </pre>
              ) : null}
            </div>
          </div>
        ) : null}

        {kind === 'none' ? (
          <div className="max-w-md w-full rounded-2xl border border-white/15 bg-white/5 px-8 py-10 text-center">
            <FileTypeIcon fileName={current.fileName} size="lg" className="mx-auto" />
            <p className="mt-4 text-white font-semibold break-words">{current.fileName || 'File'}</p>
            <p className="mt-2 text-sm text-white/50">Preview is not available. Download or open the file.</p>
          </div>
        ) : null}

        {hasMultiple ? (
          <button
            type="button"
            onClick={goNext}
            className="absolute right-2 sm:right-4 top-1/2 -translate-y-1/2 z-10 p-3 text-white bg-black/50 hover:bg-black/70 rounded-full border border-white/20"
            aria-label="Next"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M9 5l7 7-7 7" />
            </svg>
          </button>
        ) : null}
      </div>
      <ForwardMessageModal
        isOpen={showForward}
        heading="Forward image"
        overlayClassName="z-[120]"
        onClose={() => setShowForward(false)}
        onChannelSelected={handleForwardToChannel}
      />
    </div>
  );
};

export default NoteAttachmentViewer;
